import { DestroyRef, Directive, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';

import { ProviderService } from '../services/provider.service';
import { ProviderConfigService } from '../services/provider-config.service';
import { Provider, ProviderConfig } from '../models/provider.model';
import { Tenant } from '../../admin/tenants/models/tenant.model';
import { TenantService } from '../../admin/tenants/services/tenant.service';
import { BreadcrumbService } from '../../../shared/components/breadcrumbs/breadcrumb.service';
import { ToastService } from '../../../shared/services/toast-service';
import { ModalService } from '../../../shared/components/modal/modal.service';
import { TENANT_PICKER_SIZE, providerBasePath } from './provider-routing';

export type ProviderDetailTab = 'feeds' | 'details';

/**
 * Base for the per-type provider shells (/admin/providers/[<type>/]:providerId)
 * — super-admin only. Each type's shell (SiriProviderShellComponent, ...)
 * extends this, renders `ProviderShellLayoutComponent` around its router
 * outlet, and provides itself as `ProviderShell`:
 *
 *   providers: [{ provide: ProviderShell, useExisting: forwardRef(() => XProviderShellComponent) }]
 *
 * so the shared layout, the tabs and the Details layout read provider, feed
 * list and tenants via DI instead of refetching. Type-specific tabs inject
 * their concrete shell for anything extra (e.g. SIRI diagnostics).
 */
@Directive()
export abstract class ProviderShell implements OnInit {
  protected readonly providerService = inject(ProviderService);
  protected readonly configService = inject(ProviderConfigService);
  protected readonly tenantService = inject(TenantService);
  protected readonly breadcrumbs = inject(BreadcrumbService);
  protected readonly toastService = inject(ToastService);
  protected readonly modal = inject(ModalService);
  protected readonly transloco = inject(TranslocoService);
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly router = inject(Router);
  protected readonly route = inject(ActivatedRoute);

  /** The route tree this shell is mounted on: a provider type, or null for the generic tree. */
  abstract readonly routeType: string | null;

  readonly providerId = this.route.snapshot.paramMap.get('providerId') ?? '';

  readonly provider = signal<Provider | null>(null);
  readonly feeds = signal<ProviderConfig[]>([]);
  readonly tenants = signal<Tenant[]>([]);
  readonly isFeedsLoading = signal(false);

  readonly tenantNameById = computed(() => new Map(this.tenants().map((t) => [t.id, t.name])));

  readonly feedsByTenant = computed(() => {
    const names = this.tenantNameById();
    const counts = new Map<string, number>();
    for (const feed of this.feeds()) {
      counts.set(feed.tenantId, (counts.get(feed.tenantId) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([tenantId, count]) => ({ tenantId, name: names.get(tenantId) ?? tenantId, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  readonly tenantCount = computed(() => this.feedsByTenant().length);

  /** Which tab the child outlet is showing, derived from the active route. */
  readonly activeTab = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.currentTab()),
    ),
    { initialValue: this.currentTab() },
  );

  ngOnInit(): void {
    this.fetchProvider();
    this.fetchFeeds();
    this.fetchTenants();
  }

  /** Router commands for this provider's base page (its Feeds tab). */
  basePath(): string[] {
    return providerBasePath(this.routeType, this.providerId);
  }

  /** Absolute navigation — relative paths are ambiguous from a ''-path shell. */
  showTab(tab: ProviderDetailTab): void {
    void this.router.navigate(tab === 'details' ? [...this.basePath(), 'details'] : this.basePath());
  }

  addFeed(): void {
    void this.router.navigate([...this.basePath(), 'new']);
  }

  async toggleEnabled(): Promise<void> {
    const provider = this.provider();
    if (!provider) return;
    if (provider.enabled) {
      const confirmed = await this.modal.confirm({
        kind: 'destructive',
        destructive: true,
        title: this.transloco.translate('providers.detailPage.disable.title'),
        message: this.transloco.translate('providers.detailPage.disable.message', { name: provider.name }),
        confirmLabel: this.transloco.translate('providers.detailPage.disable.confirm'),
        confirmWord: provider.name,
      });
      if (!confirmed) return;
    }
    this.setEnabled(provider, !provider.enabled);
  }

  /** Subclasses that hold per-feed data (e.g. diagnostics) extend this to refresh it too. */
  refetchFeeds(): void {
    this.fetchFeeds();
  }

  refetchProvider(): void {
    this.fetchProvider();
  }

  private setEnabled(provider: Provider, enabled: boolean): void {
    this.providerService
      .update(provider.id, { enabled })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.transloco.translate('providers.toast.updated'), 'success');
          this.fetchProvider();
        },
        error: () => this.toastService.show(this.transloco.translate('providers.toast.updateFailed'), 'error'),
      });
  }

  private fetchProvider(): void {
    if (!this.providerId) return;
    this.providerService
      .get(this.providerId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.provider.set(res.data);
          // Covers hard refresh / direct links, where the list never named it.
          this.breadcrumbs.setLabel('providerId', res.data.id, res.data.name);
        },
        error: () => this.toastService.show(this.transloco.translate('providers.toast.loadFailed'), 'error'),
      });
  }

  private fetchFeeds(): void {
    if (!this.providerId) return;
    this.isFeedsLoading.set(true);
    this.configService
      .getAll(this.providerId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.feeds.set(res.data);
          this.isFeedsLoading.set(false);
        },
        error: () => {
          this.toastService.show(this.transloco.translate('providers.feedsGrid.toast.loadFailed'), 'error');
          this.isFeedsLoading.set(false);
        },
      });
  }

  private fetchTenants(): void {
    this.tenantService
      .getTenants(0, TENANT_PICKER_SIZE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.tenants.set(res.data),
        error: () => {},
      });
  }

  private currentTab(): ProviderDetailTab {
    return this.route.snapshot.firstChild?.routeConfig?.path === 'details' ? 'details' : 'feeds';
  }
}
